<template>
    <div class="flex flex-col min-h-screen">
        <Transition v-if="isLoading"
                    name="spinner">
            <div id="cover-spin">
                <span class="loader">
                    <span>{{ $spinnerText }}</span>
                </span>
            </div>
        </Transition>

        <div class="mt-auto">
            <nav class="bg-gray-800">
                <div class="mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="flex h-16 items-center justify-between">
                        <div class="flex items-center">
                            <Menubar ref="menubar"
                                     :unstyled="false"
                                     :model="menuItems"
                                     :breakpoint="'0px'">
                                <template #start>
                                    <div class="shrink-0">
                                        <img :src="menuLogoWhite"
                                             class="h-8 mr-8" />
                                    </div>
                                </template>

                                <template #item="{ item, props, hasSubmenu, root }">
                                    <div class="hidden md:block">
                                        <div class="flex items-baseline space-x-4">
                                            <span v-if="(!item?.hidden || isCreator) && (Object.values(unprotectedMenuItems).includes(item?.fullPath?.toLowerCase().replace(/[_\s]/g, '-')) || hasPermission(item?.fullPath))">
                                                <Link v-ripple
                                                      v-if="item.route"
                                                      :href="checkRoute(item.route, null, item.label)"
                                                      :id="item.id"
                                                      :style="item.itemstyle"
                                                      class=""
                                                      style=""
                                                      v-bind="props.action">
                                                    <div inert
                                                         class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-300">
                                                        <i :class="item.icon"
                                                           :style="item.iconstyle"></i>
                                                        <span :class="item.labelclass"
                                                              :style="item.labelstyle">{{ __(item.label) }}</span>
                                                        <div v-if="item.description"
                                                             class="text-xs text-gray-400">
                                                            <span v-html="item.description"></span>
                                                        </div>
                                                    </div>
                                                </Link>

                                                <div v-ripple
                                                     v-else
                                                     class=""
                                                     style=""
                                                     :style="item.itemstyle"
                                                     v-bind="props.action">
                                                    <div class="flex items-center gap-2 w-full rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white">
                                                        <i :class="item.icon"
                                                           :style="item.iconstyle"></i>
                                                        <span :class="item.labelclass"
                                                              :style="item.labelstyle">{{ __(item.label) }}</span>
                                                        <i v-if="hasSubmenu"
                                                           :class="['pi text-xs', { 'pi-angle-down': root, 'pi-angle-right': !root }]"
                                                           :style="item.submenustyle"></i>
                                                        <div v-if="item.description"
                                                             class="text-xs text-gray-400"
                                                             :id="item.id">
                                                            <span v-html="item.description"></span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </span>
                                        </div>
                                    </div>
                                </template>
                            </Menubar>
                        </div>

                        <div class="hidden md:block">
                            <div class="ml-4 flex items-center md:ml-6">

                                <AlertNotifier />

                                <button v-if="multiModuleApp"
                                        type="button"
                                        class="relative rounded-full p-1 text-gray-400 hover:text-white focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500"
                                        @click="selectModule"
                                        v-tooltip.bottom="__('Select module')">
                                    <span class="absolute -inset-1.5"></span>
                                    <span class="sr-only">{{ __('Select module') }}</span>
                                    <i class="pi pi-th-large"></i>
                                </button>

                                <div class="relative ml-3"
                                     ref="topUserDropdownRef">
                                    <button @click="showTopUserDropdown = !showTopUserDropdown"
                                            class="relative flex max-w-xs items-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500">
                                        <span class="absolute -inset-1.5"></span>
                                        <span class="sr-only">Open user menu</span>
                                        <div class="size-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm outline -outline-offset-1 outline-white/10">
                                            {{ userName?.charAt(0) || 'U' }}
                                        </div>
                                    </button>

                                    <div v-if="showTopUserDropdown"
                                         class="absolute right-0 mt-2 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-50 py-2 min-w-[200px]">
                                        <div class="px-4 py-3 border-b border-gray-700">
                                            <div class="flex items-center gap-3">
                                                <div class="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white text-base font-medium">
                                                    {{ userName?.charAt(0) || 'U' }}
                                                </div>
                                                <div class="flex-1 min-w-0">
                                                    <div class="text-sm font-medium text-white truncate">{{ userName }}</div>
                                                    <div class="text-xs text-gray-400 truncate">{{ userEmail }}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <Link :href="checkRoute('user-profile', { user_id: userId })"
                                              class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
                                            <i class="pi pi-user text-gray-400"></i>
                                            <span>Profil użytkownika</span>
                                        </Link>
                                        <button @click="saveSession"
                                                class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left">
                                            <i class="pi pi-save text-gray-400"></i>
                                            <span>Zapisz sesję</span>
                                        </button>
                                        <button @click="removeSession"
                                                class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left">
                                            <i class="pi pi-trash text-gray-400"></i>
                                            <span>Usuń sesję</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="-mr-2 flex md:hidden">
                            <button type="button"
                                    class="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-white/5 hover:text-white focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500"
                                    @click="showMobileMenu = !showMobileMenu">
                                <span class="absolute -inset-0.5"></span>
                                <span class="sr-only">Open main menu</span>
                                <svg v-if="!showMobileMenu"
                                     viewBox="0 0 24 24"
                                     fill="none"
                                     stroke="currentColor"
                                     stroke-width="1.5"
                                     data-slot="icon"
                                     aria-hidden="true"
                                     class="size-6">
                                    <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                                          stroke-linecap="round"
                                          stroke-linejoin="round" />
                                </svg>
                                <svg v-else
                                     viewBox="0 0 24 24"
                                     fill="none"
                                     stroke="currentColor"
                                     stroke-width="1.5"
                                     data-slot="icon"
                                     aria-hidden="true"
                                     class="size-6">
                                    <path d="M6 18 18 6M6 6l12 12"
                                          stroke-linecap="round"
                                          stroke-linejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="showMobileMenu"
                     class="md:hidden bg-gray-900 border-t border-gray-700">
                    <div class="px-2 pt-2 pb-3 space-y-1">
                        <div v-for="item in menuItems"
                             :key="item.id">
                            <div v-if="(!item?.hidden || isCreator) && (Object.values(unprotectedMenuItems).includes(item?.fullPath?.toLowerCase().replace(/[_\s]/g, '-')) || hasPermission(item?.fullPath))">
                                <Link v-if="item.route"
                                      :href="checkRoute(item.route, null, item.label)"
                                      class="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                                      @click="showMobileMenu = false">
                                    <i :class="item.icon"
                                       class="mr-3"></i>
                                    <span>{{ __(item.label) }}</span>
                                </Link>

                                <div v-else>
                                    <button class="w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                                            @click="item.showSubmenu = !item.showSubmenu">
                                        <div class="flex items-center">
                                            <i :class="item.icon"
                                               class="mr-3"></i>
                                            <span>{{ __(item.label) }}</span>
                                        </div>
                                        <i :class="['pi text-xs', item.showSubmenu ? 'pi-angle-up' : 'pi-angle-down']"></i>
                                    </button>

                                    <div v-if="item.showSubmenu"
                                         class="pl-4 space-y-1 bg-gray-800/50">
                                        <div v-for="subitem in item.items"
                                             :key="subitem.id">
                                            <div v-if="(!subitem?.hidden || isCreator) && (Object.values(unprotectedMenuItems).includes(subitem?.fullPath?.toLowerCase().replace(/[_\s]/g, '-')) || hasPermission(subitem?.fullPath))">
                                                <div v-if="subitem.items && subitem.items.length > 0">
                                                    <button class="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
                                                            @click="subitem.showSubmenu = !subitem.showSubmenu">
                                                        <div class="flex items-center">
                                                            <i :class="subitem.icon"
                                                               class="mr-3 text-xs"></i>
                                                            <span>{{ __(subitem.label) }}</span>
                                                        </div>
                                                        <i :class="['pi text-xs', subitem.showSubmenu ? 'pi-angle-up' : 'pi-angle-down']"></i>
                                                    </button>
                                                    <div v-if="subitem.showSubmenu"
                                                         class="pl-4 space-y-1 bg-gray-800/30">
                                                        <Link v-for="subsubitem in subitem.items"
                                                              :key="subsubitem.id"
                                                              :href="checkRoute(subsubitem.route, null, subsubitem.label)"
                                                              class="flex items-center px-3 py-2 rounded-md text-xs font-medium text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
                                                              @click="showMobileMenu = false">
                                                            <i :class="subsubitem.icon"
                                                               class="mr-3 text-xs"></i>
                                                            <span>{{ __(subsubitem.label) }}</span>
                                                        </Link>
                                                    </div>
                                                </div>
                                                <Link v-else
                                                      :href="checkRoute(subitem.route, null, subitem.label)"
                                                      class="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
                                                      @click="showMobileMenu = false">
                                                    <i :class="subitem.icon"
                                                       class="mr-3 text-xs"></i>
                                                    <span>{{ __(subitem.label) }}</span>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="border-t border-gray-700 mx-4 pt-2 pb-3 flex gap-2">
                        <button v-if="multiModuleApp"
                                type="button"
                                class="flex-1 flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
                                @click="selectModule"
                                :title="__('Select module')">
                            <i class="pi pi-th-large"></i>
                            <span class="ml-2 text-sm">{{ __('Select module') }}</span>
                        </button>

                        <div class="mx-4">
                            <AlertNotifier class="flex-1 flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-white/5 hover:text-white transition-colors" />
                        </div>
                    </div>

                    <div class="border-t border-gray-700 mx-4 pt-2 pb-3">
                        <Link :href="checkRoute('user-profile', { user_id: userId })"
                              class="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
                            <i class="pi pi-user mr-2"></i>
                            Profil użytkownika
                        </Link>
                        <button @click="saveSession"
                                class="w-full text-left flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
                            <i class="pi pi-save mr-2"></i>
                            Zapisz sesję
                        </button>
                        <button @click="removeSession"
                                class="w-full text-left flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
                            <i class="pi pi-trash mr-2"></i>
                            Usuń sesję
                        </button>
                    </div>
                </div>
            </nav>
        </div>

        <div class="flex-1 bg-secondary-50">
            <div class="mx-auto w-full">
                <div class="">
                    <slot></slot>
                </div>
            </div>
        </div>

        <footer class="mt-auto bg-gray-700 py-2 shadow-inner">
            <div class="mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div class="flex items-center gap-3 text-gray-400 text-sm">
                        <img :src="developerLogo"
                             class="h-6 w-auto opacity-80" />
                        <p class="pl-3 text-gray-200/40">© 4Evolve v1.0</p>
                        <p class="text-xs text-gray-200/40">rev {{ systemRevision }}</p>
                    </div>
                    <nav class="flex items-center gap-6 text-sm">
                        <Link :href="checkRoute('dashboard-help', null, null)"
                              class="text-gray-400 hover:text-white transition-colors">
                            Pomoc </Link>
                        <Link :href="checkRoute('dashboard-changelog', null, null)"
                              class="text-gray-400 hover:text-white transition-colors"> Changelog </Link>
                        <Link :href="checkRoute('dashboard-privacy', null, null)"
                              class="text-gray-400 hover:text-white transition-colors"> Polityka prywatności </Link>
                    </nav>
                </div>
            </div>
        </footer>

        <flash-message />
    </div>
</template>

<script setup>
import Menubar from 'primevue/menubar';
import Button from 'primevue/button';
import { Link, router, usePage } from '@inertiajs/vue3';
import { ref, onMounted, onBeforeMount, watchEffect, onBeforeUnmount } from 'vue';
import Image from '@pages/Helper/Image.vue';
import { initFunctions } from '../../init.js';
import { __, saveAppSession, removeAppSession, hasPermission, can, checkRoute, getStoreValue, setStoreValue, getServerValue, getFromRoute } from '@pages/Helper/utils.js';
import { menuLogoWhite, developerLogo } from '@pages/Helper/constants.js';
import axios from 'axios';
import Menu from 'primevue/menu';
import AlertNotifier from '../App/Data/Alert/AlertNotifier.vue';

const menuItems = ref(null);
const unprotectedMenuItems = ref({});
const userName = ref(null);
const userId = ref(null);
const multiModuleApp = ref(false);
const systemRevision = ref(getStoreValue('systemRevision'));

const dataFromSession = ref(null);
const currentDate = ref(null);
const page = usePage();
const isLoading = ref(false);
const isCreator = ref(false);
const showUserDropdown = ref(false);
const showMobileMenu = ref(false);
const userMenu = ref(null);
const userEmail = ref(null);
const userMenuItems = [
    {
        label: 'Profil użytkownika',
        icon: 'pi pi-user',
        command: () => { },
    },
    {
        label: 'Zapisz sesję',
        icon: 'pi pi-save',
        command: () => {
            saveSession();
        },
    },
    {
        label: 'Usuń sesję',
        icon: 'pi pi-trash',
        command: () => {
            removeSession();
        },
    },
];
//------------------------------------------------------------------------------

watchEffect(async () => {
    let savedDataFromSession = null;
    savedDataFromSession = getStoreValue('dataFromSession');
    if (!savedDataFromSession) {
        savedDataFromSession = await getServerValue('dataFromSession');
        if (savedDataFromSession?.hasOwnProperty('dataFromSession')) {
            setStoreValue('dataFromSession', savedDataFromSession.dataFromSession);
            savedDataFromSession = savedDataFromSession.dataFromSession;
        }
    }

    if (savedDataFromSession) {
        dataFromSession.value = savedDataFromSession;
    }
});

// // ------------------------------------------------------------------------------

watchEffect(async () => {
    currentDate.value = page.props.currentDate;
});

// ------------------------------------------------------------------------------

onBeforeMount(async () => {
    const layoutMenu = page.props.menuContent;

    if (layoutMenu && layoutMenu.menu) {
        menuItems.value = layoutMenu.menu;
        unprotectedMenuItems.value = layoutMenu.unprotected;
        userName.value = layoutMenu.userName;
        userId.value = layoutMenu.userId;
        userEmail.value = layoutMenu.userEmail;
        multiModuleApp.value = layoutMenu.multiModuleApp;

        if (menuItems.value.length == 0) {
            if (multiModuleApp.value) {
                console.error('Wybrany moduł (' + layoutMenu.moduleSymbol + ') nie posiada pliku json z zawartością menu. Nastąpi przekierowanie do strony wyboru modułu');
                window.location.href = route('select-module');
            } else {
                console.error('Aplikacja nie posiada pliku json z zawartością menu.');
                window.location.href = route('app-logout');
            }
        }
    }

    clearInterval(window.displaySessionInterval);
});

//------------------------------------------------------------------------------

onMounted(async () => {
    window.endOfSession = false;
    window.showDisplaySessionTime = false;
    window.lastActivityTime = null;
    isLoading.value = true;
    await initFunctions();
    isLoading.value = false;
    isCreator.value = getStoreValue('isCreator');

    const elements = document.querySelectorAll('[aria-hidden]');
    elements.forEach((element) => {
        //element.setAttribute('aria-hidden', 'false');
        element.removeAttribute('aria-hidden');
    });
});

//------------------------------------------------------------------------------

const saveSession = () => {
    saveAppSession();
};

//------------------------------------------------------------------------------

const removeSession = () => {
    removeAppSession();
};

//------------------------------------------------------------------------------

const selectModule = () => {
    router.get(
        route('select-module', {
            module: getStoreValue('currentModule'),
        })
    );
};

//------------------------------------------------------------------------------

const userDropdownRef = ref(null);
const showTopUserDropdown = ref(false);
const topUserDropdownRef = ref(null);

const handleClickOutside = (event) => {
    if (userDropdownRef.value && !userDropdownRef.value.contains(event.target)) {
        showUserDropdown.value = false;
    }
    if (topUserDropdownRef.value && !topUserDropdownRef.value.contains(event.target)) {
        showTopUserDropdown.value = false;
    }
};

onMounted(() => {
    document.addEventListener('mousedown', handleClickOutside);
});
onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside);
});
</script>
